// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { personSchema } from '@/account/api/types';
import { redactedFieldSchema, responseMetadataSchema } from '@/api/types';
import { currencySchema } from '@/currency/api/types';
import { isoCurrencyCodeSchema } from '@/currency/constants/iso';
import { sparseProjectSchema } from '@/sparse/api/types';
import { dollarStringSchema } from '@/utilities/currency';
import { dateSchema, dateTimeSchema } from '@/utilities/date-time-schema';
import { z } from 'zod';

/**
 * Schema for a cost response from the API
 */
export const costSchema = z.object({
  id: z.string(),
  key: z.string(),
  category: z.string().nullable(),
  amount: currencySchema.nullable(),
  description: z.string().nullable(),
  costIncurredOn: dateSchema,
  createdAt: dateTimeSchema,
  createdBy: personSchema,
  lastUpdatedAt: dateTimeSchema,
  lastUpdatedBy: personSchema,
  attachedPerson: personSchema.nullable(),
  attachedProject: sparseProjectSchema.nullable(),
});

export type Cost = z.infer<typeof costSchema>;

/**
 * Response schema for cost listing
 */
export const costListingSchema = z.object({
  results: z.array(costSchema),
  totalCount: z.number(),
});

/**
 * Schema for a time record response from the API
 */
const timeRecordSchema = z.object({
  id: z.string(),
  key: z.string(),
  category: z.string(),
  totalAmount: redactedFieldSchema(currencySchema.nullable()),
  totalMinutes: z.number().nullable(),
  rate: redactedFieldSchema(currencySchema.nullable()),
  description: z.string().nullable(),
  incurredOn: dateSchema,
  worker: personSchema,
  attachedProject: sparseProjectSchema.nullable(),
  createdAt: dateTimeSchema,
  lastUpdatedAt: dateTimeSchema,
  createdBy: personSchema,
  lastUpdatedBy: personSchema,
});

export type TimeRecord = z.infer<typeof timeRecordSchema>;

/**
 * Response schema for time record listing
 */
export const timeRecordListingSchema = z.object({
  results: z.array(timeRecordSchema),
  totalCount: z.number(),
});

/**
 * Request schema for creating a time record
 */
export const createUpdateTimeRecordSchema = z.object({
  totalMinutes: z.number().min(1).max(1440),
  hourlyRate: currencySchema.nullish(),
  category: z.string().min(1),
  description: z.string().nullable(),
  incurredOn: dateSchema,
  workerId: z.string(),
  projectId: z.string().nullable(),
});

export type CreateUpdateTimeRecord = z.infer<
  typeof createUpdateTimeRecordSchema
>;

/**
 * Response schema for time record create/update operations
 */
export const timeRecordResponseSchema = z
  .object({
    data: timeRecordSchema,
    metadata: responseMetadataSchema,
  })
  .transform(({ data, ...response }) => ({ ...response, timeRecord: data }));

export type TimeRecordResponse = z.infer<typeof timeRecordResponseSchema>;

const isoCurrencyCodeBucketSchema = z.preprocess(
  (v) => (typeof v === 'string' ? v.toUpperCase() : v),
  isoCurrencyCodeSchema
);

// Like isoCurrencyCodeBucketSchema but also accepts an empty iso code, emitted for entries
// with no rate/amount currency (and excluded from the currency selector).
const caseInsensitiveIsoCurrencyCodeSchema = z.preprocess(
  (v) => (typeof v === 'string' ? v.toUpperCase() : v),
  isoCurrencyCodeSchema.or(z.literal(''))
);

/**
 * Per-currency time record hours, entry count, and money totals. Money fields are
 * decimal strings, redacted when the caller lacks time.rate.
 */
const currencyTimeRecordSummarySchema = z.object({
  isoCode: isoCurrencyCodeBucketSchema,
  totalMinutes: z.number(),
  totalEntries: z.number(),
  unconvertedEntries: z.number(),
  totalValue: redactedFieldSchema(dollarStringSchema.nullable()),
  averageHourlyRate: redactedFieldSchema(dollarStringSchema.nullable()),
});

/**
 * Schema for the time record aggregate response. totalMinutes and totalEntries are
 * the cross-currency grand totals; hours, entries, and money are also bucketed per
 * currency in byCurrency (the no-rate remainder is grand total minus the buckets).
 * organizationCurrencyTotal is unredacted and converts rated entries into a currency
 * value, leaving out unratedEntries and unconvertedEntries.
 */
export const timeRecordAggregateSchema = z.object({
  organizationCurrencyTotal: currencySchema.nullable(),
  unratedEntries: z.number(),
  unconvertedEntries: z.number(),
  totalMinutes: z.number(),
  totalEntries: z.number(),
  byCurrency: z
    .array(currencyTimeRecordSummarySchema)
    .nullable()
    .transform((v) => v ?? []),
});

export type TimeRecordAggregate = z.infer<typeof timeRecordAggregateSchema>;

/**
 * Per-currency cost entry count and money totals. Money fields are decimal strings.
 */
const currencyCostSummarySchema = z.object({
  unconvertedEntries: z.number(),
  isoCode: isoCurrencyCodeBucketSchema,
  totalEntries: z.number(),
  totalAmount: dollarStringSchema.nullable(),
  averageAmount: dollarStringSchema.nullable(),
});

/**
 * Schema for the cost aggregate response. totalEntries is the cross-currency grand
 * total; entries and money are also bucketed per currency in byCurrency (the
 * no-currency remainder is grand total minus the buckets).
 */
export const costAggregateSchema = z.object({
  organizationCurrencyTotal: currencySchema.nullable(),
  unconvertedEntries: z.number(),
  totalEntries: z.number(),
  byCurrency: z
    .array(currencyCostSummarySchema)
    .nullable()
    .transform((v) => v ?? []),
});

export type CostAggregate = z.infer<typeof costAggregateSchema>;

/**
 * Schema for a cost time series point
 */
const costTimeSeriesPointSchema = z.object({
  dates: z.array(dateSchema),
  workers: z.array(personSchema).nullable(),
  projects: z.array(sparseProjectSchema).nullable(),
  categories: z.array(z.string()).nullable(),
  isoCode: caseInsensitiveIsoCurrencyCodeSchema,
  totalAmount: dollarStringSchema.nullable(),
  totalEntries: z.number(), // Int64 from backend comes as number in JS
  averageAmount: dollarStringSchema.nullable(),
});

export type CostTimeSeriesPoint = z.infer<typeof costTimeSeriesPointSchema>;

/**
 * Schema for cost time series response
 */
export const costTimeSeriesSchema = z.object({
  points: z.array(costTimeSeriesPointSchema),
  interval: z.string(),
  groupBy: z.string(),
});

export type CostTimeSeries = z.infer<typeof costTimeSeriesSchema>;

/**
 * Schema for a time record time series point
 */
const timeRecordTimeSeriesPointSchema = z.object({
  dates: z.array(dateSchema),
  workers: z.array(personSchema).nullable(),
  projects: z.array(sparseProjectSchema).nullable(),
  categories: z.array(z.string()).nullable(),
  isoCode: caseInsensitiveIsoCurrencyCodeSchema,
  totalAmount: redactedFieldSchema(dollarStringSchema.nullable()), // Dollar value, redacted if no time.rate permission
  totalMinutes: z.number().nullable(), // Time value in minutes
  totalEntries: z.number(),
  averageAmount: redactedFieldSchema(dollarStringSchema.nullable()), // Average dollar value, redacted if no time.rate permission
  averageMinutes: z.number().nullable(), // Average minutes per entry
});

export type TimeRecordTimeSeriesPoint = z.infer<
  typeof timeRecordTimeSeriesPointSchema
>;

/**
 * Schema for time record time series response
 */
export const timeRecordTimeSeriesSchema = z.object({
  points: z.array(timeRecordTimeSeriesPointSchema),
  interval: z.string(),
  groupBy: z.string(),
});

export type TimeRecordTimeSeries = z.infer<typeof timeRecordTimeSeriesSchema>;
